import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Refresh } from "iconsax-react";
import { FieldHint } from "../../component/global/DBFieldBlock";
import { quickSetupPayload } from "./helpers";
import { incentivesApi } from "./api";
import "./styles.scss";

export const createQuickIncentive = (value, target) =>
    value.enabled
        ? incentivesApi.createQuickCampaign(quickSetupPayload(value, target))
        : Promise.resolve(null);

export const defaultQuickSetup = (targetType = "item") => ({
    enabled: false,
    action: targetType === "survey" ? "survey_submit" : "feedback_submit",
    audience: "all",
    groupId: "",
    amount: 10,
    budget: 1000,
});

export default function IncentiveQuickSetup({
    value,
    onChange,
    targetType = "item",
    partialFailure,
    retrying,
    onRetry,
}) {
    const { t } = useTranslation();
    const update = (patch) => onChange({ ...value, ...patch });

    useEffect(() => {
        if (!value.action) {
            update({
                action:
                    targetType === "survey"
                        ? "survey_submit"
                        : "feedback_submit",
            });
        }
    }, [targetType]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <section className="incentive-quick-setup">
            <div className="incentive-quick-heading">
                <div>
                    <h4>{t("incentives.quick.title")}</h4>
                    <p>{t("incentives.quick.description")}</p>
                </div>
                <label className="incentive-switch">
                    <input
                        type="checkbox"
                        role="switch"
                        checked={value.enabled}
                        onChange={(event) =>
                            update({ enabled: event.target.checked })
                        }
                    />
                    <span>{t("incentives.quick.enable")}</span>
                    <FieldHint hint={t("incentives.quick.enableHint")} />
                </label>
            </div>
            {value.enabled && (
                <div className="incentive-quick-fields">
                    <label>
                        <span>{t("incentives.fields.action")}</span>
                        <FieldHint hint={t("incentives.fields.actionHint")} />
                        <select
                            value={value.action}
                            onChange={(event) =>
                                update({ action: event.target.value })
                            }
                        >
                            <option value="feedback_submit">
                                {t("incentives.actions.feedback")}
                            </option>
                            <option value="survey_submit">
                                {t("incentives.actions.survey")}
                            </option>
                            <option value="offer">
                                {t("incentives.actions.offer")}
                            </option>
                            <option value="introduction">
                                {t("incentives.actions.introduction")}
                            </option>
                            <option value="share">
                                {t("incentives.actions.share")}
                            </option>
                        </select>
                    </label>
                    {(value.action === "introduction" ||
                        value.action === "share") && (
                        <p className="incentive-field-hint">
                            {t(`incentives.actions.${value.action}Hint`)}
                        </p>
                    )}
                    <label>
                        <span>{t("incentives.fields.audience")}</span>
                        <FieldHint hint={t("incentives.fields.audienceHint")} />
                        <select
                            value={value.audience}
                            onChange={(event) =>
                                update({ audience: event.target.value })
                            }
                        >
                            <option value="all">
                                {t("incentives.audience.all")}
                            </option>
                            <option value="group">
                                {t("incentives.audience.group")}
                            </option>
                        </select>
                    </label>
                    {value.audience === "group" && (
                        <label>
                            <span>{t("incentives.fields.groupId")}</span>
                            <FieldHint hint={t("incentives.fields.groupIdsHint")} />
                            <input
                                value={value.groupId}
                                onChange={(event) =>
                                    update({ groupId: event.target.value })
                                }
                            />
                        </label>
                    )}
                    <label>
                        <span>{t("incentives.quick.amount")}</span>
                        <FieldHint hint={t("incentives.quick.amountHint")} />
                        <input
                            type="number"
                            min="1"
                            value={value.amount}
                            onChange={(event) =>
                                update({ amount: event.target.value })
                            }
                        />
                    </label>
                    <label>
                        <span>{t("incentives.quick.budget")}</span>
                        <FieldHint hint={t("incentives.quick.budgetHint")} />
                        <input
                            type="number"
                            min="1"
                            value={value.budget}
                            onChange={(event) =>
                                update({ budget: event.target.value })
                            }
                        />
                    </label>
                    <Link
                        className="incentive-advanced-link"
                        to="/customer-club?tab=points&section=campaigns"
                    >
                        {t("incentives.quick.advanced")}
                    </Link>
                </div>
            )}
            {partialFailure && (
                <div className="incentive-partial-failure" role="alert">
                    <p>
                        <strong>{t("incentives.quick.partialTitle")}</strong>
                        {t("incentives.quick.partialDescription")}
                    </p>
                    <button
                        type="button"
                        disabled={retrying}
                        onClick={onRetry}
                    >
                        <Refresh size={18} aria-hidden="true" />
                        {t("incentives.quick.retry")}
                    </button>
                </div>
            )}
        </section>
    );
}
